import type {CountryPermissions} from '@/types';

/**
 * The two country permissions anything actually reads, and how each behaves
 * when it has never been set.
 *
 * Both are read by the MOBILE APP and by nothing on the server. They are real
 * levers, not dead fields, but they work by hiding options rather than by
 * refusing a request, so they are not a substitute for the currency kill
 * switches on the Currencies tab. See COUNTRY_AVAILABILITY_ENFORCEMENT_NOTE
 * below.
 *
 * In its own module rather than beside the modal that edits them: a file that
 * exports both a component and a constant loses its Fast Refresh boundary
 * (`react-refresh/only-export-components`), and both the modal and the
 * read-only Availability tab render this list. Same split as
 * `businessCountryFlags.ts`.
 *
 * `CountryPermissions` carries eight fields. `canSignup`, `canDeposit`,
 * `canSwap`, `canRefer` and `canTransfer` are seeded onto every country and
 * read by nothing in any package, so a switch for them would move a stored
 * value and change no behaviour. `canScheduleTransaction` IS read by the app,
 * but it enables the recurring-send entry point and is OR-ed with a feature
 * flag (`isRecurringSendEnabled || permissions.canScheduleTransaction`), so
 * turning it off closes nothing. None of the six belong on a screen someone
 * uses to shut a corridor.
 *
 * They still survive an edit: the detail endpoint ships the whole
 * `permissions` subtree and the modal echoes it back, because the write is a
 * `$set` that replaces the subtree entire.
 */
export type CountryAvailabilityPermission = 'canSend' | 'canWithdraw';

export const COUNTRY_AVAILABILITY_PERMISSIONS: Array<{
  key: CountryAvailabilityPermission;
  label: string;
  hint: string;
  /**
   * What the app does with this field when the country does not carry it.
   *
   * The two disagree, which is why they cannot share one rule the way the
   * currency kill switches do. The app reads `canWithdraw` as
   * `permissions?.canWithdraw !== false` (unset allows) and `canSend` as
   * `permissions?.canSend ?? false` (unset denies). Rendering both as "unset
   * means allowed" would tell ops that an unconfigured country still offers
   * payouts, which for `canSend` is the opposite of the truth.
   */
  defaultWhenUnset: boolean;
}> = [
  {
    key: 'canSend',
    label: 'Payouts allowed',
    hint: 'Gates every payout destination the app offers for this country: bank account, mobile money, Interac, UPI, WeChat and Alipay. Turning it off empties the destination screen in builds already released, with no app update.',
    defaultWhenUnset: false,
  },
  {
    key: 'canWithdraw',
    label: 'Bank payouts allowed',
    hint: 'Narrower: whether this country stays in the Stripe-backed bank payout list. Leaves the other destination channels alone.',
    defaultWhenUnset: true,
  },
];

/**
 * Shown once above the two switches rather than repeated in each hint.
 *
 * Both are read by the mobile app and by nothing on the server, which decides
 * whether they hold under pressure: they remove options from a screen, so a
 * request that arrives anyway is still accepted. That is the opposite of the
 * currency switches, which reject server-side. Anyone closing a corridor needs
 * to know which kind they are touching before they decide they are done.
 */
export const COUNTRY_AVAILABILITY_ENFORCEMENT_NOTE =
  'These two are enforced by the mobile app, not the server: they hide payout options rather than rejecting transactions. To actually stop money moving, use Corridor Availability on the Currencies tab, which rejects the transaction server-side.';

/**
 * The value the app will act on, which is the stored value or this field's own
 * default when there is none. `??` rather than `||` so an explicit `false` is
 * preserved, and so a `null` from an older record falls through to the default
 * exactly as `undefined` does.
 */
export const resolvePermission = (
  permissions: CountryPermissions | undefined,
  key: CountryAvailabilityPermission,
  defaultWhenUnset: boolean,
): boolean => permissions?.[key] ?? defaultWhenUnset;
